import React, { useState, useEffect, useCallback } from "react";
import axiosClient, { linkImage } from "../../../axios-client";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "../../../context/ContextProvider";
import { useColorModeValue } from "@chakra-ui/react";

const CURRENCY_COLORS = {
  MAD: "#10b981", USD: "#3b82f6", EUR: "#8b5cf6", GBP: "#f59e0b",
  AED: "#06b6d4", SAR: "#ef4444", DZD: "#84cc16", TND: "#f97316", EGP: "#ec4899",
};

// ─── Inline SVG Icons ──────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
  </svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" strokeLinecap="round" />
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 11v6M14 11v6" strokeLinecap="round" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeLinecap="round" />
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
    <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <line x1="12" y1="12" x2="12" y2="12.01" /><path d="M8 12h.01M16 12h.01M8 16h.01M12 16h.01M16 16h.01" strokeLinecap="round" />
  </svg>
);

export default function GestBusinesses() {
  const navigate = useNavigate();
  const { user, currentBusiness, setCurrentBusiness } = useStateContext();
  const isAdminClient = user?.role === "AdminClient";

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  // Dark mode colors
  const bg = useColorModeValue("#f8fafc", "#1a202c");
  const cardBg = useColorModeValue("#ffffff", "#2d3748");
  const textColor = useColorModeValue("#1e293b", "#f7fafc");
  const secondaryText = useColorModeValue("#64748b", "#94a3b8");
  const borderColor = useColorModeValue("#e2e8f0", "#4a5568");
  const headerBg = useColorModeValue("#ffffff", "#2d3748");
  const inputBg = useColorModeValue("#f8fafc", "#1a202c");
  const hoverBg = useColorModeValue("#f1f5f9", "#374151");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchBusinesses = useCallback(() => {
    setLoading(true);
    axiosClient
      .get("/businesses/")
      .then((r) => setBusinesses(r.data.results || r.data || []))
      .catch(() => showToast("Erreur lors du chargement des businesses.", "error"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const handleDelete = () => {
    if (!deleteModal) return;
    setDeleting(true);
    axiosClient
      .delete(`/businesses/${deleteModal.id}/`)
      .then(() => {
        showToast(`Business "${deleteModal.name}" supprimé avec succès.`);
        // If the deleted business was currently selected, clear it
        if (currentBusiness?.id === deleteModal.id) {
          setCurrentBusiness(null);
        }
        setBusinesses((prev) => prev.filter((b) => b.id !== deleteModal.id));
        setDeleteModal(null);
      })
      .catch(() => showToast("Erreur lors de la suppression.", "error"))
      .finally(() => setDeleting(false));
  };

  const handleSelect = (biz) => {
    if (!biz.is_active) {
      showToast(`Le business "${biz.name}" est inactif et ne peut pas être sélectionné.`, "error");
      return;
    }
    setCurrentBusiness(biz);
    showToast(`Business "${biz.name}" sélectionné.`);
  };

  const filtered = businesses.filter((b) =>
    b.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", background: bg }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 24, zIndex: 9999,
          background: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
          color: toast.type === "error" ? "#dc2626" : "#166534",
          borderRadius: 10, padding: "12px 20px", fontWeight: 500, fontSize: 14,
          boxShadow: "0 8px 30px rgba(0,0,0,.12)",
        }}>
          {toast.msg}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "rgba(0,0,0,.45)", display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: cardBg, borderRadius: 16, padding: "32px 36px",
            maxWidth: 440, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,.3)",
            border: `1px solid ${borderColor}`,
          }}>
            <div style={{ fontSize: 38, textAlign: "center", marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: "0 0 10px", textAlign: "center", color: textColor, fontSize: 18, fontWeight: 700 }}>
              Supprimer le Business
            </h2>
            <p style={{ margin: "0 0 8px", color: secondaryText, fontSize: 14, textAlign: "center", lineHeight: 1.6 }}>
              Êtes-vous sûr de vouloir supprimer <strong style={{ color: textColor }}>"{deleteModal.name}"</strong> ?
            </p>
            <p style={{ margin: "0 0 24px", color: "#dc2626", fontSize: 13, textAlign: "center", lineHeight: 1.5 }}>
              ⚠️ Cette action supprimera définitivement toutes les données associées (catégories, produits, commandes, etc.) et ne peut pas être annulée.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setDeleteModal(null)}
                disabled={deleting}
                style={{
                  flex: 1, padding: "11px", border: `1px solid ${borderColor}`,
                  borderRadius: 10, background: cardBg, color: secondaryText,
                  cursor: "pointer", fontWeight: 600, fontSize: 14,
                }}>
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  flex: 1, padding: "11px", border: "none",
                  borderRadius: 10, background: deleting ? "#fca5a5" : "#dc2626",
                  color: "#fff", cursor: deleting ? "not-allowed" : "pointer",
                  fontWeight: 600, fontSize: 14,
                }}>
                {deleting ? "Suppression..." : "🗑️ Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: textColor }}>🏢 Mes Businesses</h1>
          <p style={{ margin: "4px 0 0", color: secondaryText, fontSize: 13 }}>
            {businesses.length} business{businesses.length !== 1 ? "es" : ""} au total
          </p>
        </div>
        {isAdminClient && (
          <button
            onClick={() => navigate("/ajouter-business")}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 20px",
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff",
              border: "none", borderRadius: 10, fontWeight: 600, fontSize: 14,
              cursor: "pointer", boxShadow: "0 4px 14px rgba(99,102,241,.35)",
            }}>
            <PlusIcon /> Nouveau Business
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 24, maxWidth: 400 }}>
        <div style={{ position: "relative" }}>
          <span style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: secondaryText, display: "flex", alignItems: "center",
          }}>
            <SearchIcon />
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un business..."
            style={{
              width: "100%", padding: "10px 14px 10px 38px",
              border: `1px solid ${borderColor}`, borderRadius: 10,
              background: inputBg, color: textColor, fontSize: 14,
              outline: "none", boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: "center", padding: "60px 0", color: secondaryText }}>
          <div style={{
            width: 40, height: 40, border: "3px solid #e2e8f0",
            borderTop: "3px solid #6366f1", borderRadius: "50%",
            animation: "spin 0.8s linear infinite", margin: "0 auto 16px",
          }} />
          <p style={{ margin: 0, fontSize: 14 }}>Chargement des businesses...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <div style={{
          textAlign: "center", padding: "60px 0",
          background: cardBg, borderRadius: 16, border: `1px solid ${borderColor}`,
        }}>
          <div style={{ color: secondaryText, marginBottom: 12 }}><BuildingIcon /></div>
          <h3 style={{ margin: "0 0 8px", color: textColor, fontSize: 16, fontWeight: 600 }}>
            {search ? "Aucun résultat trouvé" : "Aucun business"}
          </h3>
          <p style={{ margin: "0 0 20px", color: secondaryText, fontSize: 13 }}>
            {search
              ? `Aucun business ne correspond à "${search}".`
              : "Créez votre premier business pour commencer."}
          </p>
          {!search && isAdminClient && (
            <button
              onClick={() => navigate("/ajouter-business")}
              style={{
                padding: "10px 22px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                color: "#fff", border: "none", borderRadius: 10,
                fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>
              + Créer un Business
            </button>
          )}
        </div>
      )}

      {/* Business Grid */}
      {!loading && filtered.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 20,
        }}>
          {filtered.map((biz) => {
            const isSelected = currentBusiness?.id === biz.id;
            const currencyColor = CURRENCY_COLORS[biz.currency] || "#6366f1";

            return (
              <div
                key={biz.id}
                style={{
                  background: cardBg,
                  borderRadius: 16,
                  border: isSelected
                    ? "2px solid #6366f1"
                    : `1px solid ${borderColor}`,
                  boxShadow: isSelected
                    ? "0 4px 20px rgba(99,102,241,.2)"
                    : "0 2px 10px rgba(0,0,0,.06)",
                  overflow: "hidden",
                  transition: "box-shadow .2s, border-color .2s",
                  position: "relative",
                }}>

                {/* Selected badge */}
                {isSelected && (
                  <div style={{
                    position: "absolute", top: 12, right: 12,
                    background: "#6366f1", color: "#fff",
                    padding: "2px 10px", borderRadius: 99,
                    fontSize: 11, fontWeight: 700,
                  }}>
                    ✓ Actuel
                  </div>
                )}

                {/* Card Header */}
                <div style={{ padding: "20px 20px 16px", display: "flex", alignItems: "center", gap: 14 }}>
                  {biz.logo ? (
                    <img
                      src={`${linkImage}${biz.logo}`}
                      alt={biz.name}
                      style={{ width: 52, height: 52, borderRadius: 12, objectFit: "cover", border: `2px solid ${borderColor}` }}
                    />
                  ) : (
                    <div style={{
                      width: 52, height: 52, borderRadius: 12, flexShrink: 0,
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontWeight: 700, fontSize: 22,
                    }}>
                      {biz.name?.charAt(0)?.toUpperCase() || "B"}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {biz.name}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                      <span style={{
                        padding: "1px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700,
                        background: currencyColor + "1a", color: currencyColor,
                      }}>
                        {biz.currency}
                      </span>
                      <span style={{
                        padding: "1px 10px", borderRadius: 99, fontSize: 11, fontWeight: 700,
                        background: biz.is_active ? "#dcfce7" : "#fef2f2",
                        color: biz.is_active ? "#16a34a" : "#dc2626",
                      }}>
                        {biz.is_active ? "✓ Actif" : "✕ Inactif"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {biz.description && (
                  <div style={{
                    padding: "0 20px 14px", fontSize: 13, color: secondaryText,
                    lineHeight: 1.5, overflow: "hidden",
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>
                    {biz.description}
                  </div>
                )}

                {/* Stats Row */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  borderTop: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}`,
                }}>
                  {[
                    { label: "Commandes", value: biz.orders_count ?? 0, icon: "📦" },
                    { label: "Produits", value: biz.products_count ?? 0, icon: "🛒" },
                    { label: "Membres", value: biz.members_count ?? 0, icon: "👥" },
                  ].map(({ label, value, icon }) => (
                    <div key={label} style={{ padding: "12px 8px", textAlign: "center" }}>
                      <div style={{ fontSize: 16, marginBottom: 2 }}>{icon}</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: textColor }}>{value}</div>
                      <div style={{ fontSize: 10, color: secondaryText }}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ padding: "14px 16px", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {/* Select Button */}
                  {!isSelected && (
                    <button
                      onClick={() => handleSelect(biz)}
                      disabled={!biz.is_active}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        border: biz.is_active ? `1px solid #6366f1` : `1px solid ${borderColor}`,
                        borderRadius: 8,
                        background: "transparent",
                        color: biz.is_active ? "#6366f1" : secondaryText,
                        cursor: biz.is_active ? "pointer" : "not-allowed",
                        opacity: biz.is_active ? 1 : 0.5,
                        transition: "background .15s",
                      }}
                      onMouseOver={e => biz.is_active && (e.currentTarget.style.background = "#6366f11a")}
                      onMouseOut={e => biz.is_active && (e.currentTarget.style.background = "transparent")}
                    >
                      Sélectionner
                    </button>
                  )}

                  {/* View Button */}
                  <button
                    onClick={() => navigate("/business-detail", { state: { business: biz } })}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                      padding: "8px 12px", border: `1px solid ${borderColor}`,
                      borderRadius: 8, background: "transparent", color: secondaryText,
                      cursor: "pointer", fontWeight: 600, fontSize: 12,
                      transition: "background .15s",
                    }}
                    onMouseOver={e => e.currentTarget.style.background = hoverBg}
                    onMouseOut={e => e.currentTarget.style.background = "transparent"}
                  >
                    <EyeIcon /> Détail
                  </button>

                  {/* Edit Button (AdminClient only) */}
                  {isAdminClient && (
                    <button
                      onClick={() => navigate("/modifier-business", { state: { business: biz } })}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                        padding: "8px 12px", border: `1px solid ${borderColor}`,
                        borderRadius: 8, background: "transparent", color: secondaryText,
                        cursor: "pointer", fontWeight: 600, fontSize: 12,
                        transition: "background .15s",
                      }}
                      onMouseOver={e => e.currentTarget.style.background = hoverBg}
                      onMouseOut={e => e.currentTarget.style.background = "transparent"}
                    >
                      <EditIcon /> Modifier
                    </button>
                  )}

                  {/* Delete Button (AdminClient only) */}
                  {isAdminClient && (
                    <button
                      onClick={() => setDeleteModal({ id: biz.id, name: biz.name })}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                        padding: "8px 12px", border: "1px solid #fca5a5",
                        borderRadius: 8, background: "transparent", color: "#dc2626",
                        cursor: "pointer", fontWeight: 600, fontSize: 12,
                        transition: "background .15s",
                      }}
                      onMouseOver={e => e.currentTarget.style.background = "#fef2f2"}
                      onMouseOut={e => e.currentTarget.style.background = "transparent"}
                    >
                      <TrashIcon /> Supprimer
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
